import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Users, ShoppingCart, Eye, CheckCircle, AlertTriangle, FileText, Edit, Trash2 } from 'lucide-react';
import OrderSheetGenerator from '../components/OrderSheetGenerator';
import CustomerDetails from '../components/CustomerDetails';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, doc, query, where, deleteDoc } from 'firebase/firestore';

const Orders = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [showAddOrderModal, setShowAddOrderModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [companyDetails, setCompanyDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editCustomerData, setEditCustomerData] = useState({});
  const [editingOrder, setEditingOrder] = useState(null);

  const [newCustomer, setNewCustomer] = useState({
    shopName: '',
    city: '',
    phoneNumber: ''
  });

  const [newOrder, setNewOrder] = useState({
    customerId: '',
    customerName: '',
    customerPhone: '',
    customerCity: '',
    items: [],
    discount: 0,
    paymentMethod: 'cheque',
    status: 'pending',
    showNewCustomerForm: false
  });

  const [currentItem, setCurrentItem] = useState({
    productId: '',
    pairsNeeded: '',
    pairsPerPack: '',
    note: ''
  });
  const [productSearch, setProductSearch] = useState('');
  const [showProductSuggestions, setShowProductSuggestions] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [customersSnapshot, ordersSnapshot, inventorySnapshot, companySnapshot] = await Promise.all([
        getDocs(collection(db, 'customers')),
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'inventory')),
        getDocs(collection(db, 'company'))
      ]);
      
      const customersData = customersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const ordersData = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const inventoryData = inventorySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const companyData = companySnapshot.docs.length > 0 ? companySnapshot.docs[0].data() : {
        name: 'ADDICT',
        address: '123 Business Street',
        city: 'Colombo, Sri Lanka',
        phone: '+94 11 234 5678',
        email: 'info@addict.lk'
      };
      
      setCustomers(customersData);
      setOrders(ordersData);
      setInventory(inventoryData);
      setCompanyDetails(companyData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="inventory-page">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await updateDoc(doc(db, 'customers', editingCustomer.id), editCustomerData);
        setCustomers(customers.map(c => c.id === editingCustomer.id ? {...c, ...editCustomerData} : c));
        setEditingCustomer(null);
        setEditCustomerData({});
      } else {
        const customer = {
          ...newCustomer,
          createdAt: new Date().toISOString()
        };
        
        const docRef = await addDoc(collection(db, 'customers'), customer);
        const newCustomerWithId = { id: docRef.id, ...customer };
        
        setCustomers([...customers, newCustomerWithId]);
        
        if (showAddOrderModal) {
          setNewOrder({...newOrder, customerId: docRef.id, customerName: customer.shopName});
        }
      }
      
      setNewCustomer({ shopName: '', city: '', phoneNumber: '' });
      setShowAddCustomerModal(false);
    } catch (error) {
      console.error('Error saving customer:', error);
    }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setEditCustomerData({...customer});
    setShowAddCustomerModal(true);
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      await deleteDoc(doc(db, 'customers', customerId));
      setCustomers(customers.filter(c => c.id !== customerId));
    } catch (error) {
      console.error('Error deleting customer:', error);
    }
  };

  const handleAddItemToOrder = async () => {
    const pairsNeeded = parseInt(currentItem.pairsNeeded) || 0;
    const pairsPerPack = parseInt(currentItem.pairsPerPack) || 0;
    
    if (!currentItem.productId || pairsNeeded <= 0 || pairsPerPack <= 0) return;
    
    const product = inventory.find(p => p.id === currentItem.productId);
    if (!product) return;

    const totalPairsAvailable = (product.packsInStock * product.pairsPerPack) + (product.loosePairs || 0);
    const packsNeeded = pairsNeeded;
    const packSize = pairsPerPack;
    const totalPairsNeeded = packsNeeded * packSize;
    
    // Calculate reserved quantities from existing orders
    const reservedQuery = query(collection(db, 'orders'), where('status', 'in', ['pending', 'in-production', 'ready']));
    const reservedSnapshot = await getDocs(reservedQuery);
    let totalReserved = 0;
    
    reservedSnapshot.docs.forEach(doc => {
      const order = doc.data();
      if (order.items) {
        order.items.forEach(item => {
          if (item.articleNumber === product.articleNumber) {
            totalReserved += item.totalPairs || 0;
          }
        });
      }
    });
    
    const availableForReservation = totalPairsAvailable - totalReserved;
    
    // Calculate packs and loose pairs needed
    const fullPacksNeeded = Math.floor(totalPairsNeeded / packSize);
    const loosePairsNeeded = totalPairsNeeded % packSize;
    
    // Check stock availability including reservations
    const isOutOfStock = totalPairsNeeded > availableForReservation;
    if (isOutOfStock) {
      const message = availableForReservation <= 0 
        ? `All ${totalPairsAvailable} pairs are reserved. You need ${totalPairsNeeded} pairs. This will require production.`
        : `Only ${availableForReservation} pairs available (${totalReserved} reserved). You need ${totalPairsNeeded} pairs. This will require production.`;
      
      if (!window.confirm(`${message} Continue?`)) {
        return;
      }
    }

    const priceTotal = product.pricePerPair * totalPairsNeeded;
    const displayText = fullPacksNeeded > 0 && loosePairsNeeded > 0 
      ? `${fullPacksNeeded} packs + ${loosePairsNeeded} pairs (${totalPairsNeeded} total)`
      : fullPacksNeeded > 0 
        ? `${fullPacksNeeded} packs (${totalPairsNeeded} pairs)`
        : `${loosePairsNeeded} pairs`;

    const existingItem = newOrder.items.find(item => item.productId === currentItem.productId);
    if (existingItem) {
      const newTotalPairs = existingItem.totalPairs + totalPairsNeeded;
      const newFullPacks = Math.floor(newTotalPairs / packSize);
      const newLoosePairs = newTotalPairs % packSize;
      const newDisplayText = newFullPacks > 0 && newLoosePairs > 0 
        ? `${newFullPacks} packs + ${newLoosePairs} pairs (${newTotalPairs} total)`
        : newFullPacks > 0 
          ? `${newFullPacks} packs (${newTotalPairs} pairs)`
          : `${newLoosePairs} pairs`;
      
      const updatedItems = newOrder.items.map(item => 
        item.productId === currentItem.productId 
          ? { 
              ...item, 
              totalPairs: newTotalPairs,
              fullPacks: newFullPacks,
              loosePairs: newLoosePairs,
              priceTotal: item.priceTotal + priceTotal,
              displayText: newDisplayText,
              stockStatus: (newTotalPairs > totalPairsAvailable) ? 'out-of-stock' : 'in-stock'
            }
          : item
      );
      setNewOrder({ ...newOrder, items: updatedItems });
    } else {
      setNewOrder({
        ...newOrder,
        items: [...newOrder.items, {
          productId: currentItem.productId,
          articleNumber: product.articleNumber,
          colour: product.colour,
          pricePerPair: product.pricePerPair,
          totalPairs: totalPairsNeeded,
          fullPacks: fullPacksNeeded,
          loosePairs: loosePairsNeeded,
          priceTotal: priceTotal,
          displayText: displayText,
          productionStatus: 'pending',
          stockStatus: isOutOfStock ? 'out-of-stock' : 'in-stock',
          packSize: packSize,
          note: currentItem.note?.replace(/["'\\]/g, '') || '',
          reservedQuantity: totalPairsNeeded
        }]
      });
    }
    setCurrentItem({ productId: '', pairsNeeded: '', pairsPerPack: '', note: '' });
    setProductSearch('');
  };

  const handleCompleteOrder = async (e) => {
    e.preventDefault();
    if (newOrder.items.length === 0) return;

    try {
      let customerId = newOrder.customerId;
      let customerName = newOrder.customerName;

      if (newOrder.showNewCustomerForm && newCustomer.shopName && newCustomer.city) {
        const customer = {
          ...newCustomer,
          createdAt: new Date().toISOString()
        };
        const docRef = await addDoc(collection(db, 'customers'), customer);
        const newCustomerWithId = { id: docRef.id, ...customer };
        setCustomers([...customers, newCustomerWithId]);
        
        customerId = docRef.id;
        customerName = customer.shopName;
        
        setNewCustomer({ shopName: '', city: '', phoneNumber: '' });
      }

      const subtotal = newOrder.items.reduce((sum, item) => sum + item.priceTotal, 0);
      const total = subtotal - (subtotal * newOrder.discount / 100);

      const order = {
        customerId,
        customerName,
        items: newOrder.items,
        discount: newOrder.discount,
        paymentMethod: newOrder.paymentMethod,
        status: 'pending',
        subtotal,
        total,
        createdAt: new Date().toISOString(),
        createdBy: user.username || user.email
      };

      const orderDocRef = await addDoc(collection(db, 'orders'), order);
      const newOrderWithId = { id: orderDocRef.id, ...order };
      setOrders([...orders, newOrderWithId]);
      
      setNewOrder({ customerId: '', customerName: '', items: [], discount: 0, paymentMethod: 'cheque', status: 'pending', showNewCustomerForm: false });
      setShowAddOrderModal(false);
    } catch (error) {
      console.error('Error completing order:', error);
    }
  };

  const handleMarkItemProduced = async (orderId, itemIndex) => {
    try {
      const order = orders.find(o => o.id === orderId);
      const updatedItems = [...order.items];
      const item = updatedItems[itemIndex];
      
      let producedPairs = item.totalPairs;
      
      // If item was out of stock, ask how many pairs were produced
      if (item.stockStatus === 'out-of-stock') {
        const input = prompt(`How many pairs did you produce for ${item.articleNumber} - ${item.colour}?`, item.totalPairs);
        if (input === null) return; // User cancelled
        producedPairs = parseInt(input) || 0;
        if (producedPairs <= 0) {
          alert('Please enter a valid number of pairs produced.');
          return;
        }
      }
      
      // Mark item as completed - keep reservation until sale or cancellation
      updatedItems[itemIndex] = {
        ...item,
        productionStatus: 'completed',
        producedPairs: producedPairs,
        completedAt: new Date().toISOString(),
        reservedQuantity: item.totalPairs // Ensure reservation is maintained for all items
      };
      
      // Only update inventory if item was out of stock (production needed)
      if (item.stockStatus === 'out-of-stock') {
        // Update inventory with produced quantity
        const inventoryQuery = query(collection(db, 'inventory'), where('articleNumber', '==', item.articleNumber));
        const inventorySnapshot = await getDocs(inventoryQuery);
        
        if (!inventorySnapshot.empty) {
          const inventoryDoc = inventorySnapshot.docs[0];
          const inventoryData = inventoryDoc.data();
          const packSize = inventoryData.pairsPerPack || 8;
          const currentPacks = inventoryData.packsInStock || 0;
          const currentLoosePairs = inventoryData.loosePairs || 0;
          
          // Calculate new inventory after adding produced pairs
          const totalCurrentPairs = (currentPacks * packSize) + currentLoosePairs;
          const newTotalPairs = totalCurrentPairs + producedPairs;
          const newFullPacks = Math.floor(newTotalPairs / packSize);
          const newLoosePairs = newTotalPairs % packSize;
          
          await updateDoc(doc(db, 'inventory', inventoryDoc.id), {
            packsInStock: newFullPacks,
            loosePairs: newLoosePairs,
            lastUpdated: new Date()
          });
          
          // Update local inventory state
          const updatedInventory = inventory.map(invItem => 
            invItem.id === inventoryDoc.id ? 
            { ...invItem, packsInStock: newFullPacks, loosePairs: newLoosePairs } : 
            invItem
          );
          setInventory(updatedInventory);
        }
      }
      
      // Check if all items are completed
      const allCompleted = updatedItems.every(item => item.productionStatus === 'completed');
      const newStatus = allCompleted ? 'ready' : 'in-production';
      
      await updateDoc(doc(db, 'orders', orderId), {
        items: updatedItems,
        status: newStatus
      });
      
      // Update local orders state
      const updatedOrders = orders.map(order => 
        order.id === orderId ? { ...order, items: updatedItems, status: newStatus } : order
      );
      setOrders(updatedOrders);
    } catch (error) {
      console.error('Error marking item as produced:', error);
    }
  };

  const handleConvertToSale = async (orderId) => {
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order || order.status !== 'ready') return;
      
      // Check if sale already exists for this order
      const existingSales = await getDocs(query(collection(db, 'sales'), where('orderId', '==', orderId)));
      if (!existingSales.empty) {
        alert('Sale already exists for this order!');
        return;
      }
      
      // Create sale record
      const sale = {
        customerId: order.customerId,
        customerName: order.customerName,
        items: order.items,
        discount: order.discount,
        paymentMethod: order.paymentMethod,
        paymentStatus: order.paymentMethod === 'cheque' ? 'not_paid' : 'full_paid',
        subtotal: order.subtotal,
        total: order.total,
        orderId: orderId,
        createdAt: new Date().toISOString(),
        createdBy: user.username || user.email
      };
      
      await addDoc(collection(db, 'sales'), sale);
      
      // Update order status to completed (this clears reservations)
      await updateDoc(doc(db, 'orders', orderId), {
        status: 'completed'
      });
      
      // Update local state
      const updatedOrders = orders.map(order => 
        order.id === orderId ? { ...order, status: 'completed' } : order
      );
      setOrders(updatedOrders);
      
      alert('Order converted to sale successfully! Inventory deducted and reservations cleared.');
    } catch (error) {
      console.error('Error converting to sale:', error);
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setNewOrder({
      customerId: order.customerId,
      customerName: order.customerName,
      customerPhone: order.customerPhone || '',
      customerCity: order.customerCity || '',
      items: [...order.items],
      discount: order.discount,
      paymentMethod: order.paymentMethod,
      status: order.status,
      showNewCustomerForm: false
    });
    setShowAddOrderModal(true);
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This will free up all reserved quantities.')) {
      return;
    }
    
    try {
      const order = orders.find(o => o.id === orderId);
      
      // Delete related sales first
      const salesQuery = query(collection(db, 'sales'), where('orderId', '==', orderId));
      const salesSnapshot = await getDocs(salesQuery);
      
      for (const saleDoc of salesSnapshot.docs) {
        await deleteDoc(doc(db, 'sales', saleDoc.id));
      }
      
      // Remove order from customer record
      if (order && order.customerId) {
        const customer = customers.find(c => c.id === order.customerId);
        if (customer && customer.orders) {
          const updatedOrders = customer.orders.filter(orderIdRef => orderIdRef !== orderId);
          await updateDoc(doc(db, 'customers', order.customerId), {
            orders: updatedOrders
          });
        }
      }
      
      // Delete the order
      await deleteDoc(doc(db, 'orders', orderId));
      
      // Update local state
      setOrders(orders.filter(order => order.id !== orderId));
      
      alert('Order deleted successfully!');
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Error deleting order. Please try again.');
    }
  };

  const handleUpdateOrder = async (e) => {
    e.preventDefault();
    if (newOrder.items.length === 0) return;

    try {
      const subtotal = newOrder.items.reduce((sum, item) => sum + item.priceTotal, 0);
      const total = subtotal - (subtotal * newOrder.discount / 100);

      const updatedOrder = {
        customerId: newOrder.customerId,
        customerName: newOrder.customerName,
        items: newOrder.items,
        discount: newOrder.discount,
        paymentMethod: newOrder.paymentMethod,
        subtotal,
        total,
        updatedAt: new Date().toISOString(),
        updatedBy: user.username || user.email
      };

      await updateDoc(doc(db, 'orders', editingOrder.id), updatedOrder);
      
      // Update local state
      const updatedOrders = orders.map(order => 
        order.id === editingOrder.id ? { ...order, ...updatedOrder } : order
      );
      setOrders(updatedOrders);
      
      setNewOrder({ customerId: '', customerName: '', items: [], discount: 0, paymentMethod: 'cheque', status: 'pending', showNewCustomerForm: false });
      setEditingOrder(null);
      setShowAddOrderModal(false);
      
      alert('Order updated successfully!');
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Error updating order. Please try again.');
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.shopName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOrders = orders.filter(o => {
    const orderNumber = String(o.id).slice(-6).padStart(6, '0');
    return o.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           orderNumber.includes(searchTerm);
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#f39c12';
      case 'in-production': return '#3498db';
      case 'ready': return '#27ae60';
      case 'completed': return '#95a5a6';
      default: return '#666';
    }
  };

  return (
    <div className="inventory-page">
      <div className="inventory-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} style={{ marginRight: '8px' }} />Back
          </button>
          <h1 className="inventory-title">Order Management</h1>
        </div>
        
        <div className="inventory-actions">
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
            <input
              type="text"
              placeholder="Search orders, customers, or numbers..."
              className="search-bar"
              style={{ paddingLeft: '45px' }}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowSearchSuggestions(e.target.value.length > 0);
              }}
              onFocus={() => setShowSearchSuggestions(searchTerm.length > 0)}
              onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 200)}
            />
            {showSearchSuggestions && searchTerm && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #e1e5e9',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                zIndex: 1000,
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {filteredOrders.slice(0, 5).map(order => (
                  <div
                    key={order.id}
                    style={{
                      padding: '10px 15px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f1f3f4',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}
                    onClick={() => {
                      setSearchTerm(order.customerName);
                      setShowSearchSuggestions(false);
                      setActiveTab('orders');
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.background = 'white'}
                  >
                    <span>#{String(order.id).slice(-6).padStart(6, '0')} - {order.customerName}</span>
                    <span style={{ color: '#666', fontSize: '0.9rem' }}>{order.status}</span>
                  </div>
                ))}
                {filteredCustomers.slice(0, 3).map(customer => (
                  <div
                    key={customer.id}
                    style={{
                      padding: '10px 15px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f1f3f4',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}
                    onClick={() => {
                      setSearchTerm(customer.shopName);
                      setShowSearchSuggestions(false);
                      setActiveTab('customers');
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.background = 'white'}
                  >
                    <span>{customer.shopName}</span>
                    <span style={{ color: '#666', fontSize: '0.9rem' }}>{customer.city}</span>
                  </div>
                ))}
                {filteredOrders.length === 0 && filteredCustomers.length === 0 && (
                  <div style={{ padding: '15px', color: '#666', textAlign: 'center' }}>
                    No results found
                  </div>
                )}
              </div>
            )}
          </div>
          <button className="btn btn-primary" onClick={() => setShowAddOrderModal(true)}>
            <ShoppingCart size={16} style={{ marginRight: '8px' }} />New Order
          </button>
          <button className="btn btn-secondary" onClick={() => setShowAddCustomerModal(true)}>
            <Users size={16} style={{ marginRight: '8px' }} />Add Customer
          </button>
        </div>
      </div>

      <div style={{ padding: '20px 30px', background: '#f8f9fa' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button 
            onClick={() => setActiveTab('orders')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'orders' ? '#667eea' : '#e9ecef',
              color: activeTab === 'orders' ? 'white' : '#666',
              cursor: 'pointer'
            }}
          >
            Orders
          </button>
          <button 
            onClick={() => setActiveTab('customers')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'customers' ? '#667eea' : '#e9ecef',
              color: activeTab === 'customers' ? 'white' : '#666',
              cursor: 'pointer'
            }}
          >
            Customers
          </button>
        </div>

        {activeTab === 'orders' && (
          <div className="inventory-grid">
            {filteredOrders.map(order => (
              <div key={order.id} className="inventory-item" style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #e9ecef'
              }}>
                <div className="item-header">
                  <div className="item-article">
                    {order.customerName}
                    <br />
                    <small style={{ fontSize: '0.7rem', color: '#666' }}>#{String(order.id).slice(-6).padStart(6, '0')}</small>
                  </div>
                  <div className="item-stock" style={{ color: getStatusColor(order.status), fontSize: '0.7rem' }}>
                    {order.status.toUpperCase()}
                  </div>
                </div>
                <div className="item-details">
                  <div className="item-detail">
                    <span className="item-detail-label">Status:</span>
                    <span className="item-detail-value" style={{ color: getStatusColor(order.status) }}>{order.status.toUpperCase()}</span>
                  </div>
                  <div className="item-detail">
                    <span className="item-detail-label">Customer:</span>
                    <span className="item-detail-value">{order.customerName}</span>
                  </div>
                  <div className="item-detail">
                    <span className="item-detail-label">Items:</span>
                    <span className="item-detail-value">{order.items?.length} products</span>
                  </div>
                  <div className="item-detail">
                    <span className="item-detail-label">Total:</span>
                    <span className="item-detail-value">LKR {order.total?.toLocaleString()}</span>
                  </div>
                  <div className="item-detail">
                    <span className="item-detail-label">Date:</span>
                    <span className="item-detail-value">{new Date(order.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                
                {/* Production Status */}
                {order.items && order.items.length > 0 && (
                  <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <h5 style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>Production Status:</h5>
                    {order.items.map((item, index) => (
                      <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                        <span style={{ fontSize: '0.8rem' }}>{item.articleNumber} - {item.colour}</span>
                        {item.productionStatus === 'completed' ? (
                          <CheckCircle size={16} color="#27ae60" />
                        ) : (
                          <button
                            onClick={() => handleMarkItemProduced(order.id, index)}
                            style={{
                              background: '#3498db',
                              color: 'white',
                              border: 'none',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              cursor: 'pointer'
                            }}
                          >
                            Mark Done
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee', flexWrap: 'wrap' }}>
                  <button onClick={() => setSelectedOrder(order)} style={{ background: '#667eea', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem' }}>
                    <FileText size={12} /> Sheet
                  </button>
                  {order.status !== 'completed' && (
                    <button onClick={() => handleEditOrder(order)} style={{ background: '#f39c12', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem' }}>
                      <Edit size={12} /> Edit
                    </button>
                  )}
                  <button onClick={() => handleDeleteOrder(order.id)} style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem' }}>
                    <Trash2 size={12} /> Delete
                  </button>
                  {order.status === 'ready' && (
                    <button onClick={() => handleConvertToSale(order.id)} style={{ background: '#27ae60', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem' }}>
                      <CheckCircle size={12} /> To Sale
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'customers' && (
          <div className="inventory-grid">
            {filteredCustomers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <h3>No customers found</h3>
                <p>Add your first customer to get started.</p>
              </div>
            ) : (
              filteredCustomers.map(customer => (
                <div key={customer.id} className="inventory-item">
                  <div className="item-header">
                    <div className="item-article">{customer.shopName}</div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>{customer.city}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee', flexWrap: 'wrap' }}>
                    <button onClick={() => setSelectedCustomer(customer)} style={{ background: '#3498db', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem' }}>
                      <Eye size={12} /> View
                    </button>
                    <button onClick={() => handleEditCustomer(customer)} style={{ background: '#f39c12', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem' }}>
                      <Edit size={12} /> Edit
                    </button>
                    <button onClick={() => handleDeleteCustomer(customer.id)} style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem' }}>
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddCustomerModal(false);
          setEditingCustomer(null);
          setEditCustomerData({});
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h2>
            <form onSubmit={handleAddCustomer} className="modal-form">
              <div className="form-group">
                <label>Shop Name</label>
                <input 
                  type="text" 
                  value={editingCustomer ? editCustomerData.shopName : newCustomer.shopName} 
                  onChange={(e) => editingCustomer ? 
                    setEditCustomerData({...editCustomerData, shopName: e.target.value}) :
                    setNewCustomer({...newCustomer, shopName: e.target.value})
                  } 
                  placeholder="Enter shop name"
                  required 
                />
              </div>
              <div className="form-group">
                <label>City</label>
                <input 
                  type="text" 
                  value={editingCustomer ? editCustomerData.city : newCustomer.city} 
                  onChange={(e) => editingCustomer ? 
                    setEditCustomerData({...editCustomerData, city: e.target.value}) :
                    setNewCustomer({...newCustomer, city: e.target.value})
                  } 
                  placeholder="Enter city"
                  required 
                />
              </div>
              <div className="form-group">
                <label>Phone Number (Optional)</label>
                <input 
                  type="text" 
                  value={editingCustomer ? (editCustomerData.phoneNumber || '') : newCustomer.phoneNumber} 
                  onChange={(e) => editingCustomer ? 
                    setEditCustomerData({...editCustomerData, phoneNumber: e.target.value}) :
                    setNewCustomer({...newCustomer, phoneNumber: e.target.value})
                  } 
                  placeholder="Enter phone number"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowAddCustomerModal(false);
                  setEditingCustomer(null);
                  setEditCustomerData({});
                }}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingCustomer ? 'Update Customer' : 'Add Customer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Order Modal */}
      {showAddOrderModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddOrderModal(false);
          setEditingOrder(null);
          setNewOrder({ customerId: '', customerName: '', items: [], discount: 0, paymentMethod: 'cheque', status: 'pending', showNewCustomerForm: false });
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">{editingOrder ? 'Edit Order' : 'New Order'}</h2>
            <form onSubmit={editingOrder ? handleUpdateOrder : handleCompleteOrder} className="modal-form">
              <div className="form-group">
                <label>Customer</label>
                {!newOrder.showNewCustomerForm ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        placeholder="Search customers..."
                        value={newOrder.customerName}
                        onChange={(e) => {
                          const searchValue = e.target.value;
                          setNewOrder({...newOrder, customerName: searchValue, customerId: ''});
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid #e1e5e9',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                      {newOrder.customerName && !newOrder.customerId && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #e1e5e9',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          zIndex: 1000,
                          maxHeight: '200px',
                          overflowY: 'auto'
                        }}>
                          {customers
                            .filter(customer => 
                              customer.shopName?.toLowerCase().includes(newOrder.customerName.toLowerCase()) ||
                              customer.city?.toLowerCase().includes(newOrder.customerName.toLowerCase())
                            )
                            .slice(0, 5)
                            .map(customer => (
                              <div
                                key={customer.id}
                                style={{
                                  padding: '10px 15px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f3f4',
                                  display: 'flex',
                                  justifyContent: 'space-between'
                                }}
                                onClick={() => {
                                  setNewOrder({
                                    ...newOrder,
                                    customerId: customer.id,
                                    customerName: customer.shopName,
                                    customerPhone: customer.phoneNumber || '',
                                    customerCity: customer.city || ''
                                  });
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                                onMouseLeave={(e) => e.target.style.background = 'white'}
                              >
                                <div>
                                  <div style={{ fontWeight: '600' }}>{customer.shopName}</div>
                                  <div style={{ fontSize: '0.85rem', color: '#666' }}>{customer.city}</div>
                                </div>
                              </div>
                            ))
                          }
                          {customers.filter(customer => 
                            customer.shopName?.toLowerCase().includes(newOrder.customerName.toLowerCase()) ||
                            customer.city?.toLowerCase().includes(newOrder.customerName.toLowerCase())
                          ).length === 0 && (
                            <div style={{ padding: '15px', color: '#666', textAlign: 'center' }}>
                              No customers found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <button 
                      type="button" 
                      onClick={() => setNewOrder({...newOrder, showNewCustomerForm: true, customerId: '', customerName: ''})}
                      className="btn btn-secondary"
                      style={{ alignSelf: 'flex-start' }}
                    >
                      + Add New Customer
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="form-group">
                      <label>Shop Name</label>
                      <input 
                        type="text" 
                        value={newCustomer.shopName} 
                        onChange={(e) => setNewCustomer({...newCustomer, shopName: e.target.value})} 
                        placeholder="Enter shop name"
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label>City</label>
                      <input 
                        type="text" 
                        value={newCustomer.city} 
                        onChange={(e) => setNewCustomer({...newCustomer, city: e.target.value})} 
                        placeholder="Enter city"
                        required 
                      />
                    </div>
                    <button 
                      type="button" 
                      onClick={() => setNewOrder({...newOrder, showNewCustomerForm: false})}
                      className="btn btn-secondary"
                      style={{ marginTop: '10px' }}
                    >
                      Use Existing Customer
                    </button>
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label>Add Products</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={(e) => {
                        setProductSearch(e.target.value);
                        setShowProductSuggestions(e.target.value.length > 0);
                      }}
                      onFocus={() => setShowProductSuggestions(productSearch.length > 0)}
                      onBlur={() => setTimeout(() => setShowProductSuggestions(false), 200)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e1e5e9',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                    {showProductSuggestions && productSearch && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid #e1e5e9',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        {inventory
                          .filter(product => 
                            product.articleNumber?.toLowerCase().includes(productSearch.toLowerCase()) ||
                            product.colour?.toLowerCase().includes(productSearch.toLowerCase())
                          )
                          .slice(0, 8)
                          .map(product => {
                            const totalPairs = (product.packsInStock * product.pairsPerPack) + (product.loosePairs || 0);
                            const fullPacks = Math.floor(totalPairs / (product.pairsPerPack || 8));
                            const loosePairs = totalPairs % (product.pairsPerPack || 8);
                            
                            // Calculate reserved quantity for this product
                            let reservedPairs = 0;
                            orders.forEach(order => {
                              if (order.status !== 'completed' && order.items) {
                                order.items.forEach(item => {
                                  if (item.articleNumber === product.articleNumber && item.productionStatus !== 'completed') {
                                    reservedPairs += item.totalPairs || 0;
                                  }
                                });
                              }
                            });
                            
                            const availablePairs = totalPairs - reservedPairs;
                            
                            return (
                              <div
                                key={product.id}
                                style={{
                                  padding: '10px 15px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f3f4',
                                  display: 'flex',
                                  justifyContent: 'space-between'
                                }}
                                onClick={() => {
                                  setCurrentItem({
                                    ...currentItem,
                                    productId: product.id,
                                    pairsPerPack: (product.pairsPerPack || 8).toString()
                                  });
                                  setProductSearch(`${product.articleNumber} - ${product.colour}`);
                                  setShowProductSuggestions(false);
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                                onMouseLeave={(e) => e.target.style.background = 'white'}
                              >
                                <div>
                                  <div style={{ fontWeight: '600' }}>{product.articleNumber} - {product.colour}</div>
                                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                    Stock: {fullPacks} packs + {loosePairs} pairs
                                    {reservedPairs > 0 && ` | Reserved: ${reservedPairs}`}
                                    <br />Available: {availablePairs} pairs
                                    {availablePairs <= 0 && ' - FULLY RESERVED'}
                                  </div>
                                </div>
                                <div style={{ color: '#667eea', fontWeight: '600' }}>
                                  LKR {product.pricePerPair}
                                </div>
                              </div>
                            );
                          })
                        }
                        {inventory.filter(product => 
                          product.articleNumber?.toLowerCase().includes(productSearch.toLowerCase()) ||
                          product.colour?.toLowerCase().includes(productSearch.toLowerCase())
                        ).length === 0 && (
                          <div style={{ padding: '15px', color: '#666', textAlign: 'center' }}>
                            No products found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <select 
                    value={currentItem.productId} 
                    onChange={(e) => {
                      const product = inventory.find(p => p.id === e.target.value);
                      setCurrentItem({...currentItem, productId: e.target.value, pairsPerPack: product?.pairsPerPack || 8});
                      if (product) {
                        setProductSearch(`${product.articleNumber} - ${product.colour}`);
                      }
                    }}
                    style={{ display: currentItem.productId ? 'none' : 'block' }}
                  >
                    <option value="">Or select from dropdown</option>
                    {inventory.map(product => {
                      const totalPairs = (product.packsInStock * product.pairsPerPack) + (product.loosePairs || 0);
                      const fullPacks = Math.floor(totalPairs / 8);
                      const loosePairs = totalPairs % 8;
                      
                      // Calculate reserved quantity
                      let reservedPairs = 0;
                      orders.forEach(order => {
                        if (order.status !== 'completed' && order.items) {
                          order.items.forEach(item => {
                            if (item.articleNumber === product.articleNumber && item.productionStatus !== 'completed') {
                              reservedPairs += item.totalPairs || 0;
                            }
                          });
                        }
                      });
                      
                      const availablePairs = totalPairs - reservedPairs;
                      
                      return (
                        <option key={product.id} value={product.id}>
                          {product.articleNumber} - {product.colour} (Stock: {fullPacks} packs + {loosePairs} pairs, Available: {availablePairs})
                          {availablePairs <= 0 && ' - FULLY RESERVED'}
                        </option>
                      );
                    })}
                  </select>
                  
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input 
                      type="number" 
                      value={currentItem.pairsNeeded} 
                      onFocus={(e) => {
                        e.target.select();
                        setTimeout(() => e.target.select(), 0);
                      }}
                      onClick={(e) => e.target.select()}
                      onChange={(e) => setCurrentItem({...currentItem, pairsNeeded: e.target.value})} 
                      min="1" 
                      placeholder="Packs needed"
                      style={{ flex: '1 1 100px', minWidth: '100px', maxWidth: '120px' }}
                    />
                    <input 
                      type="number" 
                      value={currentItem.pairsPerPack} 
                      onFocus={(e) => {
                        e.target.select();
                        setTimeout(() => e.target.select(), 0);
                      }}
                      onClick={(e) => e.target.select()}
                      onChange={(e) => setCurrentItem({...currentItem, pairsPerPack: e.target.value})} 
                      min="1" 
                      placeholder="Pairs per pack"
                      style={{ flex: '1 1 100px', minWidth: '100px', maxWidth: '120px' }}
                    />
                    <input 
                      type="text" 
                      value={currentItem.note} 
                      onChange={(e) => setCurrentItem({...currentItem, note: e.target.value})} 
                      placeholder="Note (optional)"
                      style={{ flex: '1 1 120px', minWidth: '120px' }}
                    />
                    <button 
                      type="button" 
                      onClick={handleAddItemToOrder} 
                      className="btn btn-primary"
                      style={{ flex: '0 0 auto', minWidth: '70px', padding: '8px 12px' }}
                    >
                      Add
                    </button>
                  </div>
                  
                  {currentItem.productId && (() => {
                    const product = inventory.find(p => p.id === currentItem.productId);
                    const pairsNeeded = parseInt(currentItem.pairsNeeded) || 0;
                    const packSize = parseInt(currentItem.pairsPerPack) || 0;
                    
                    if (product && pairsNeeded > 0 && packSize > 0) {
                      const totalPairsAvailable = (product.packsInStock * product.pairsPerPack) + (product.loosePairs || 0);
                      
                      // Calculate reserved quantity
                      let reservedPairs = 0;
                      orders.forEach(order => {
                        if (order.status !== 'completed' && order.items) {
                          order.items.forEach(item => {
                            if (item.articleNumber === product.articleNumber && item.productionStatus !== 'completed') {
                              reservedPairs += item.totalPairs || 0;
                            }
                          });
                        }
                      });
                      
                      const availableForReservation = totalPairsAvailable - reservedPairs;
                      const packsNeeded = pairsNeeded;
                      const totalPairsNeeded = packsNeeded * packSize;
                      const fullPacksNeeded = Math.floor(totalPairsNeeded / packSize);
                      const loosePairsNeeded = totalPairsNeeded % packSize;
                      const pricePerPair = product.pricePerPair;
                      const totalPrice = totalPairsNeeded * pricePerPair;
                      const needsProduction = totalPairsNeeded > availableForReservation;
                      
                      const displayText = fullPacksNeeded > 0 && loosePairsNeeded > 0 
                        ? `${fullPacksNeeded} packs + ${loosePairsNeeded} pairs`
                        : fullPacksNeeded > 0 
                          ? `${fullPacksNeeded} packs`
                          : `${loosePairsNeeded} pairs`;
                      
                      return (
                        <div style={{ 
                          fontSize: '0.9rem', 
                          color: needsProduction ? '#e74c3c' : '#666', 
                          padding: '8px', 
                          background: needsProduction ? '#ffeaea' : '#f8f9fa', 
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {needsProduction && <AlertTriangle size={16} />}
                          {displayText} = {totalPairsNeeded} total pairs | Total: LKR {totalPrice.toLocaleString()}
                          <br />Stock: {totalPairsAvailable} pairs | Reserved: {reservedPairs} pairs | Available: {availableForReservation} pairs
                          {needsProduction && ` (Need ${totalPairsNeeded - availableForReservation} more pairs - requires production)`}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                
                {newOrder.items.length > 0 && (
                  <div style={{ marginTop: '15px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#333' }}>Items in Order:</h5>
                    {newOrder.items.map((item, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '8px 0', 
                        borderBottom: index < newOrder.items.length - 1 ? '1px solid #ddd' : 'none'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', color: '#333' }}>({item.articleNumber}) {item.colour}</div>
                          <div style={{ fontSize: '0.9rem', color: '#666' }}>
                            {item.displayText} × LKR {item.pricePerPair} = LKR {item.priceTotal.toLocaleString()}
                          </div>
                          {item.stockStatus === 'out-of-stock' && (
                            <div style={{ fontSize: '0.8rem', color: '#e74c3c', fontWeight: '600' }}>
                              ⚠️ OUT OF STOCK - Will need production
                            </div>
                          )}
                        </div>
                        <button 
                          type="button" 
                          onClick={() => setNewOrder({...newOrder, items: newOrder.items.filter((_, i) => i !== index)})} 
                          style={{ 
                            background: '#e74c3c', 
                            color: 'white', 
                            border: 'none', 
                            padding: '4px 8px', 
                            borderRadius: '4px', 
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label>Discount (%)</label>
                <input 
                  type="number" 
                  value={newOrder.discount} 
                  onChange={(e) => setNewOrder({...newOrder, discount: parseFloat(e.target.value) || 0})} 
                  min="0" 
                  max="100" 
                  placeholder="0"
                />
              </div>
              
              <div className="form-group">
                <label>Payment Method</label>
                <select value={newOrder.paymentMethod} onChange={(e) => setNewOrder({...newOrder, paymentMethod: e.target.value})}>
                  <option value="cheque">Cheque</option>
                  <option value="cash">Cash</option>
                  <option value="bank">Bank Transfer</option>
                </select>
              </div>
              
              {newOrder.items.length > 0 && (
                <div style={{ 
                  background: 'linear-gradient(45deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  marginTop: '20px',
                  marginBottom: '20px' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#666' }}>
                    <span>Subtotal:</span>
                    <span>LKR {newOrder.items.reduce((sum, item) => sum + item.priceTotal, 0).toLocaleString()}</span>
                  </div>
                  {newOrder.discount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#e74c3c' }}>
                      <span>Discount ({newOrder.discount}%):</span>
                      <span>-LKR {(newOrder.items.reduce((sum, item) => sum + item.priceTotal, 0) * newOrder.discount / 100).toLocaleString()}</span>
                    </div>
                  )}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    fontWeight: '700', 
                    fontSize: '1.2rem', 
                    borderTop: '2px solid #667eea', 
                    paddingTop: '10px',
                    color: '#667eea'
                  }}>
                    <span>Total:</span>
                    <span>LKR {(newOrder.items.reduce((sum, item) => sum + item.priceTotal, 0) - (newOrder.items.reduce((sum, item) => sum + item.priceTotal, 0) * newOrder.discount / 100)).toLocaleString()}</span>
                  </div>
                </div>
              )}
              
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowAddOrderModal(false);
                  setEditingOrder(null);
                  setNewOrder({ customerId: '', customerName: '', items: [], discount: 0, paymentMethod: 'cheque', status: 'pending', showNewCustomerForm: false });
                }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={newOrder.items.length === 0}>
                  {editingOrder ? 'Update Order' : 'Create Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {selectedOrder && (
        <OrderSheetGenerator 
          order={selectedOrder} 
          companyDetails={companyDetails} 
          onClose={() => setSelectedOrder(null)} 
        />
      )}
      
      {selectedCustomer && (
        <CustomerDetails 
          customer={selectedCustomer} 
          onClose={() => setSelectedCustomer(null)} 
        />
      )}
    </div>
  );
};

export default Orders;